<script>
    import { onDestroy, onMount } from "svelte";
    import { navigate } from "svelte-routing";
    import {
        exportSubscribersToXLSX, getSubscriberByEmail, getSubscribers
    } from "../../../services/subscribersService";
    import SubscriberSearchBar from "../../SearchBars/SubscriberSearchSection.svelte";
    import SubscriberCard from "./SubscriberCard.svelte";
    import toast from "svelte-french-toast";
    import Table from "../Table.svelte";
    import { format } from "date-fns";
    import { settings } from "../../../stores/settingsStore";
    import { selectedActiveYear } from "../../../stores/selectedActiveYear";
    import {
        isCreateSubscriberModalClosable,
        renewSubscriberPromise,
        subscriberTableFilter
    } from "../../../stores/subscriberTableStore";
    import { filterDefinedValues } from "../../../utils/filterDefinedValues";
    import {
        addStringSort,
        createColumnsFromQuery,
        removeFilterFromKeys,
    } from "../../../utils/sortFunction";
    import { downloadFile } from "../../../utils/downloadFile";
    import ButtonPromise from "../../Button/ButtonPromise.svelte";
    import Button from "../../Button/Button.svelte";
    import { closeAllModals, openModal } from "svelte-modals";
    import * as yup from "yup";
    import InputTextModal from "../../Modal/InputTextModal.svelte";
    import { queryParams } from "../../../stores/settingsStore";

    let headerSort = undefined;
    let totalPages;
    let subscribers = [];
    let totalCount;
    let isLoading;

    let columns = [
        { displayName: " ", fieldName: "" },
        { displayName: "Nome", fieldName: "firstName", isSortable: true, sortValue: "" },
        { displayName: "Cognome", fieldName: "lastName", isSortable: true, sortValue: "" },
        { displayName: "Email", fieldName: "email", isSortable: true, sortValue: "" },
        { displayName: "Cellulare", fieldName: "phone.number" },
        { displayName: "Residenza", fieldName: "address" },
        { displayName: "Inserimento", fieldName: "membership_createdAt", isSortable: true, sortValue: "" },
        { displayName: "Attivo", fieldName: "active" },
        { displayName: "Data attiv.", fieldName: "activeDate" },
        { displayName: "UGS", fieldName: "isUgs" },
        { displayName: "Quota", fieldName: "paymentAmount" },
    ];
    let exportPromise = null;

    async function getSubscriberList(pagination) {
        isLoading = true;

        headerSort ? sort.push(headerSort) : null;

        if (!pagination?.referenceYear) {
            return;
        }

        getSubscribers(pagination).then((resp) => {

            if(resp.status === 200 && resp?.data) {
                subscribers = resp.data.data;

                totalPages = Math.ceil(resp.data?.totalCount / ($subscriberTableFilter?.size ?? 50));
                totalCount = resp.data.totalCount;
            }

            isLoading = false;
        }).catch((err) => {
            isLoading = false;

            if(err?.response?.data?.status === 500){
                toast.error(`Errore durante la ricerca, errore: ${err.toString()}`);
            }
        })
    }

	async function handleExportToXLSX(pagination) {
		exportPromise = (async () => {
			try {
				const data = await exportSubscribersToXLSX(pagination);
				const fileName = `iscritti_${format(new Date(), "dd_MM_yyyy")}.xlsx`;
				downloadFile(document, data.data, fileName);
				toast.success("Esportazione completata");
			} catch (err) {
                if(err?.response?.data?.status === 500){
                    toast.error(`Errore durante l'esportazione, errore: ${err.toString()}`);
                }
			} finally {
				exportPromise = null;
			}
		})();
	}

    function updateSortFilter(sorts) {
        let filteredSorts = removeFilterFromKeys($subscriberTableFilter?.sort ?? "", columns.map((c) => c.fieldName));

        sorts.forEach(sort => {
            filteredSorts = addStringSort(filteredSorts, sort.field, sort.sort);
        });

        $subscriberTableFilter = { ...$subscriberTableFilter, sort: filteredSorts};
    }

    function handleCreateSubscriber() {
        openModal(InputTextModal, {
            title: "Nuova iscrizione",
            message: `Puoi utilizzare i dati di un iscritto degli anni precedenti oppure crearne uno nuovo.`,
            valueType: yup.string().email("Inserisci una mail corretta").required("Inserisci la mail dell'iscritto"),
            placeholder: "Inserisci la mail di un iscritto",
            onSubmitPromise: renewSubscriberPromise,
            canClose: isCreateSubscriberModalClosable,
            extraButtons: [
                {
                    content: "Nuovo iscritto",
                    buttonClass: "bg-lightBlue-500 text-center font-semibold text-white w-full",
                    handleClick: () => {
                        closeAllModals()
                        navigate("/admin/create", { replace: true })
                    }
                }
            ],
            onConfirm: async (data) => {
                $renewSubscriberPromise = (async () => {
                    try {
                       const res = await getSubscriberByEmail(data);
                        $renewSubscriberPromise = null;

                        navigate(`/admin/create?id=${res.data.id}`);
                        closeAllModals();
                    } catch (err) {
                        toast.error("Email non collegata a nessun utente.")
                    } finally {
                        $renewSubscriberPromise = null;
                    }
                })()

            }
        });
    }

    const unsubscribeQueryParams = queryParams.subscribe(() => {
        // Read the live URL rather than the store's value: programmatic
        // navigate() (pushState) doesn't refresh the popstate-based store, so on
        // (re)mount we must pick up whatever is currently in the address bar —
        // e.g. the filters restored when returning here from a subscriber edit.
        const filters = Object.fromEntries(new URLSearchParams(window.location.search));

        if (!filters.page) {  filters.page = "1" }
        if (!filters.size) {  filters.size = "50" }
        if (!!filters.referenceYear) {
            $selectedActiveYear = +filters.referenceYear;
        }

        if (!!filters.sort) {
            columns = createColumnsFromQuery(filters.sort, columns);
        }

        $subscriberTableFilter = { ...filters };
    });

    const unsubscribeTableFilter = subscriberTableFilter.subscribe(async (filter) => {
        if (!filter) {
            return;
        }

        const urlSearchParams = new URLSearchParams(filterDefinedValues(filter));
        navigate(!urlSearchParams.size ? "" : `?${urlSearchParams.toString()}`, {replace: false});

        if (!!filter.referenceYear) {
            $selectedActiveYear = +filter.referenceYear;
            getSubscriberList(Object.fromEntries(urlSearchParams));
        }

    });

    let unsubscribeActiveYear;

    onMount(() => {
        unsubscribeActiveYear = selectedActiveYear.subscribe((year) => {
            $subscriberTableFilter = { ...$subscriberTableFilter, referenceYear: year };
        });
    })

    onDestroy(() => {
        unsubscribeQueryParams();
        unsubscribeTableFilter();
        unsubscribeActiveYear();

        $subscriberTableFilter = null;
    });
</script>

{#if !!$settings && $subscriberTableFilter}
    <Table
        bind:page={$subscriberTableFilter.page}
        bind:size={$subscriberTableFilter.size}
        bind:totalPages
        bind:totalCount
        bind:isLoading
        bind:rowCount={subscribers.length}
        columns={columns}
        subject="Iscritti"
        on:sort={(e) => updateSortFilter(e.detail)}
    >
        <div class="flex flex-col md:flex-row items-center justify-between w-full gap-2" slot="actions">
            <div class="relative w-full max-w-full flex-grow flex-1">
                <span class="font-semibold text-3xl light">
                    Iscritti
                </span>
            </div>

            <div class="flex flex-col w-full gap-1 md:flex-row justify-end">
                <ButtonPromise
                    bind:promise={exportPromise}
                    handleClick="{() => handleExportToXLSX({...$subscriberTableFilter, sort: undefined, page: undefined, size: undefined})}"
                    content="Esporta in XLSX"
                    buttonClass="w-full md:w-44 bg-blueGray-600"
                />

                <Button
                    handleClick="{() => handleCreateSubscriber()}"
                    buttonClass="w-full md:w-44 bg-lightBlue-500 text-white"
                    content="Crea iscritto"
                />
            </div>
        </div>

        <div slot="searchbar" class="z-40">
            {#if $subscriberTableFilter.referenceYear}
                <SubscriberSearchBar />
            {/if}
        </div>

        <svelte:fragment slot="tableContent">
            {#each subscribers as subscriber}
                <SubscriberCard
                    on:update={getSubscriberList($subscriberTableFilter)}
                    bind:subscriber
                    bind:maximumPayment={$settings.maximumPayment}
                    bind:minimumPaymentNotUGS={$settings.minimumPaymentNotUGS}
                    bind:minimumPaymentUGS={$settings.minimumPaymentUGS}
                />
            {/each}
        </svelte:fragment>
    </Table>
{/if}
